import { Button } from '@cimpress/react-components';
import { getViews, type View } from '@rendering/lookup';
import { Preview, type PreviewProps } from '@rendering/product-preview-react';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { getAuthHeaders } from '../../../Auth';
import { getProductCurrentVersion } from '../../../api';
import { parseOptionsFromSurfaceUrl, resolveSurfaceFps, resolveProductSku } from '../../../tools';
import { TENANT_TYPE_COMBINATIONS } from './constants';

export interface VortexVisualizationCardProps {
    documentUrl?: string;
    surfaceUrl?: string;
    onError?: (error: string) => void;
}

export interface SurfaceData {
    mcpSku?: string;
    isMockQA?: boolean;
    [key: string]: unknown;
}

const styles: { [key: string]: CSSProperties } = {
    container: {
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        maxHeight: '550px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    errorContainer: {
        padding: '12px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '4px',
        color: '#856404',
    },
    canvasContainer: { position: 'relative', width: '100%', height: 450 },
    canvas: { width: '100%', height: 450 },
    actionsContainer: {
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        flexFlow: 'row wrap',
        gap: '8px',
    },
    errorWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    debugPre: {
        backgroundColor: '#eaeaea',
        padding: '8px',
        borderRadius: '4px',
        fontSize: '12px',
        overflowX: 'auto',
        margin: 0,
    },
};

/**
 * Component for rendering 3D Vortex visualization.
 *
 * This component automatically resolves the correct tenant and type combinations
 * internally to find a valid 3D model view from the lookup service.
 */
export function VortexVisualizationCard({ documentUrl, surfaceUrl, onError }: VortexVisualizationCardProps) {
    const [viewData, setViewData] = useState<View | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [debugInfo, setDebugInfo] = useState<string>('');
    const [resolvedTenant, setResolvedTenant] = useState<string>('cimpress');
    const [resolvedType, setResolvedType] = useState<string>('services');

    // Track last fetched state to prevent redundant fetches
    const lastFetchRef = useRef<string>('');

    useEffect(() => {
        if (!surfaceUrl || !documentUrl) {
            return;
        }

        // Use a stable key based on the surface URL + documentUrl to prevent redundant fetches
        const fetchKey = JSON.stringify({ surfaceUrl, documentUrl });
        if (lastFetchRef.current === fetchKey) {
            return;
        }
        lastFetchRef.current = fetchKey;

        const fetchViewData = async () => {
            setError(null);
            setDebugInfo('');
            onError?.('');
            try {
                const authHeaders = await getAuthHeaders();

                // Resolve surface FPS info using the unified helper
                const resolved = await resolveSurfaceFps(surfaceUrl);
                if (!resolved) {
                    const errorMsg = 'Could not resolve product SKU from surface URL';
                    setError(errorMsg);
                    onError?.(errorMsg);
                    return;
                }

                let mcpSku: string | undefined = resolved.id;
                let productVersion: number | null = resolved.version;
                let options: Record<string, string> = resolved.options;
                const isFromLookup = resolved.source === 'lookup';
                const isFromUrlParse = resolved.source === 'url-parse';

                if (!isFromLookup && !isFromUrlParse) {
                    mcpSku = await resolveProductSku(surfaceUrl || mcpSku);

                    if (!productVersion) {
                        productVersion = await getProductCurrentVersion(mcpSku);
                    }
                    if (!productVersion) {
                        const errorMsg = `Could not resolve product version for SKU: ${mcpSku}`;
                        setError(errorMsg);
                        onError?.(errorMsg);
                        return;
                    }

                    options = parseOptionsFromSurfaceUrl(surfaceUrl);
                }

                setDebugInfo(JSON.stringify({ mcpSku, productVersion, options, source: resolved.source }, null, 2));

                const productReference = {
                    id: mcpSku,
                    attributes: options,
                    version: productVersion,
                };

                // Query combinations in parallel and resolve with the quickest successful one
                const fetchPromises = TENANT_TYPE_COMBINATIONS.map(async (combo) => {
                    const {
                        views: [view],
                    } = await getViews({
                        authorizationHeader: authHeaders.Authorization,
                        request: {
                            reference: productReference,
                            tenant: { id: combo.tenant, type: combo.type },
                            previewType: ['vortex'],
                        },
                    });
                    if (view) {
                        return { view, combo };
                    }
                    throw new Error('No view');
                });

                let quickestSuccess;
                try {
                    quickestSuccess = await Promise.any(fetchPromises);
                } catch (anyError) {
                    const errorMsg = `No Vortex 3D view found for SKU ${mcpSku} under any tenant/type combination.`;
                    setError(errorMsg);
                    onError?.(errorMsg);
                    setViewData(null);
                    return;
                }

                setResolvedTenant(quickestSuccess.combo.tenant);
                setResolvedType(quickestSuccess.combo.type);
                setViewData(quickestSuccess.view);
            } catch (e) {
                console.error('Error fetching view data for Vortex:', e);
                const errorMessage = e instanceof Error ? e.message : JSON.stringify(e);
                setError(errorMessage);
                onError?.(errorMessage);
            }
        };

        fetchViewData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surfaceUrl, documentUrl]);

    const user = useMemo<PreviewProps['user']>(
        () => ({ referer: 'ags', tenant: { id: resolvedTenant, type: resolvedType } }),
        [resolvedTenant, resolvedType],
    );

    if (!surfaceUrl || !documentUrl) {
        return null;
    }

    return (
        <div>
            <div style={styles.container}>
                {error ? (
                    <div style={styles.errorWrapper}>
                        <div style={styles.errorContainer}>
                            <strong>Vortex Error:</strong> {error}
                        </div>
                        {debugInfo && <pre style={styles.debugPre}>{debugInfo}</pre>}
                    </div>
                ) : (
                    <>
                        {documentUrl && viewData && (
                            <Preview
                                key={`${documentUrl}-${resolvedTenant}`}
                                user={user}
                                renderOptions={{
                                    vortex: 'interactive',
                                    cameraViews: true,
                                    animations: true,
                                }}
                                cimDocUrl={documentUrl}
                                view={viewData}
                            >
                                {({ Canvas, renderActions }) => (
                                    <div style={styles.canvasContainer}>
                                        <Canvas style={styles.canvas} />
                                        <div style={styles.actionsContainer}>
                                            {renderActions?.(({ actions }) =>
                                                actions.map((action) => (
                                                    <Button key={action.id} onClick={action.onAnimate}>
                                                        {action.name}
                                                    </Button>
                                                )),
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Preview>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
